import { useEffect, useState } from "react";
import api, { formatApiError, downloadFile, previewFile } from "@/lib/api";
import { formatDate, trainingTone } from "@/lib/format";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { StatusBadge } from "@/components/StatusBadge";
import { Loader2, Save, Plus, Eye, Download, Upload, Pencil, Trash2, RotateCcw } from "lucide-react";
import { Dialog as Dlg, DialogContent as DlgContent, DialogHeader as DlgHeader, DialogTitle as DlgTitle, DialogFooter as DlgFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

export default function PersonnelDetail({ personnelId, canEdit, onClose, onChanged }) {
  const [p, setP] = useState(null);
  const [trainings, setTrainings] = useState([]);
  const [docs, setDocs] = useState([]);
  const [info, setInfo] = useState({});
  const [src5, setSrc5] = useState({});
  const [nt, setNt] = useState({ training_date: "", valid_until: "", basic: "", safety: "", role: "" });
  const [certType, setCertType] = useState("temel");
  const [certFile, setCertFile] = useState(null);
  const [editTr, setEditTr] = useState(null);
  const [ef, setEf] = useState({ training_date: "", valid_until: "", basic: "", safety: "", role: "" });
  const [delTr, setDelTr] = useState(null);
  const [trash, setTrash] = useState(null);
  const [src5File, setSrc5File] = useState(null);
  const [src5Publish, setSrc5Publish] = useState(true);

  const load = async () => {
    const [d, t, dc] = await Promise.all([
      api.get(`/personnel/${personnelId}`),
      api.get(`/personnel/${personnelId}/trainings`).catch(() => ({ data: [] })),
      api.get(`/personnel/${personnelId}/documents`).catch(() => ({ data: [] })),
    ]);
    setP(d.data); setTrainings(t.data); setDocs(dc.data);
    setInfo({ name: d.data.name || "", surname: d.data.surname || "", duty: d.data.duty || "", tc: d.data.tc || "", birth_date: d.data.birth_date || "", phone: d.data.phone || "", is_active: d.data.is_active !== false, left_date: d.data.left_date || "" });
    setSrc5(d.data.src5 || {});
  };
  useEffect(() => { load().catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, [personnelId]); // eslint-disable-line

  const saveInfo = async () => {
    try { const payload = { ...info, left_date: info.is_active ? null : (info.left_date || null) }; await api.patch(`/personnel/${personnelId}`, payload); toast.success("Bilgiler kaydedildi."); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveSrc5 = async () => {
    try { await api.put(`/personnel/${personnelId}/src5`, src5); toast.success("SRC5 kaydedildi."); load(); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const addTraining = async () => {
    const scores = {};
    ["basic", "safety", "role"].forEach((k) => { if (nt[k] !== "") scores[k] = parseInt(nt[k], 10); });
    try {
      const { data } = await api.post(`/personnel/${personnelId}/trainings`, { training_date: nt.training_date, valid_until: nt.valid_until, scores });
      toast.success("Eğitim kaydı eklendi.");
      if (data.warning) toast.warning(data.warning);
      setNt({ training_date: "", valid_until: "", basic: "", safety: "", role: "" }); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const complete = async (tid) => { try { await api.post(`/trainings/${tid}/complete`); toast.success("Eğitim tamamlandı."); load(); onChanged?.(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const openEdit = (t) => {
    setEditTr(t);
    setEf({ training_date: t.training_date || "", valid_until: t.valid_until || "",
      basic: t.scores?.basic ?? "", safety: t.scores?.safety ?? "", role: t.scores?.role ?? "" });
  };
  const saveEdit = async () => {
    const scores = {};
    ["basic", "safety", "role"].forEach((k) => { if (ef[k] !== "" && ef[k] != null) scores[k] = parseInt(ef[k], 10); });
    try {
      await api.patch(`/trainings/${editTr.id}`, { training_date: ef.training_date, valid_until: ef.valid_until, scores });
      toast.success("Eğitim kaydı güncellendi."); setEditTr(null); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const doDelete = async () => {
    try {
      const { data } = await api.delete(`/trainings/${delTr.id}`);
      toast.success("Eğitim kaydı çöp kutusuna taşındı.");
      if (data.linked_docs > 0) toast.info(`${data.linked_docs} bağlı belge klasörlerinde korunuyor; otomatik silinmedi.`);
      setDelTr(null); load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const openTrash = async () => { try { const { data } = await api.get(`/personnel/${personnelId}/trainings-trash`); setTrash(data); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const restoreTr = async (tid) => { try { await api.post(`/trainings/${tid}/restore`); toast.success("Eğitim kaydı geri yüklendi."); openTrash(); load(); onChanged?.(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const uploadSrc5Doc = async () => {
    if (!src5File) { toast.error("PDF seçin."); return; }
    const fd = new FormData(); fd.append("file", src5File); fd.append("publish", String(src5Publish));
    try { await api.post(`/personnel/${personnelId}/src5-document`, fd); toast.success(src5Publish ? "SRC5 belgesi yüklendi ve yayımlandı." : "SRC5 belgesi yüklendi."); setSrc5File(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const toggleSrc5Publish = async () => {
    const d = p.src5_doc;
    try { await api.patch(`/documents/${d.id}`, { visibility: "customer", published: !d.published }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const uploadCert = async () => {
    if (!certFile) { toast.error("Dosya seçin."); return; }
    const fd = new FormData(); fd.append("file", certFile); if (certType !== "diger") fd.append("cert_type", certType); fd.append("section", "customer"); fd.append("publish", "true");
    try { await api.post(`/personnel/${personnelId}/documents`, fd); toast.success("Sertifika yüklendi ve yayımlandı."); setCertFile(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">{p ? `${p.name} ${p.surname}` : "Personel"}</DialogTitle></DialogHeader>
        {!p ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-sky-600" /></div> : (
          <Tabs defaultValue="bilgi">
            <TabsList>
              <TabsTrigger value="bilgi" data-testid="pd-tab-info">Bilgiler</TabsTrigger>
              <TabsTrigger value="egitim" data-testid="pd-tab-training">Eğitimler</TabsTrigger>
              <TabsTrigger value="belge" data-testid="pd-tab-docs">Belgeler</TabsTrigger>
              <TabsTrigger value="src5" data-testid="pd-tab-src5">SRC5</TabsTrigger>
            </TabsList>

            <TabsContent value="bilgi" className="space-y-3 pt-3">
              <StatusBadge tone={trainingTone(p.training_status)}>{p.training_status}</StatusBadge>
              <div className="grid grid-cols-2 gap-3">
                {[["name", "Ad*"], ["surname", "Soyad*"], ["duty", "Görev*"], ["tc", "T.C. Kimlik No"], ["birth_date", "Doğum Tarihi"], ["phone", "Telefon"]].map(([k, l]) => (
                  <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label>
                    <Input type={k === "birth_date" ? "date" : "text"} value={info[k] || ""} onChange={(e) => setInfo({ ...info, [k]: e.target.value })} disabled={!canEdit} data-testid={`pd-info-${k}`} /></div>
                ))}
              </div>
              {canEdit && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1"><Label className="text-xs">Çalışma Durumu</Label>
                    <select className="border rounded px-2 py-2 text-sm w-full" value={info.is_active ? "active" : "left"} onChange={(e) => setInfo({ ...info, is_active: e.target.value === "active" })} data-testid="pd-info-status">
                      <option value="active">Çalışıyor</option>
                      <option value="left">İşten ayrıldı</option>
                    </select>
                  </div>
                  {!info.is_active && (
                    <div className="space-y-1"><Label className="text-xs">Ayrılma Tarihi (ops.)</Label><Input type="date" value={info.left_date || ""} onChange={(e) => setInfo({ ...info, left_date: e.target.value })} data-testid="pd-info-left-date" /></div>
                  )}
                </div>
              )}
              {canEdit && <Button className="bg-sky-600 hover:bg-sky-700" onClick={saveInfo} data-testid="pd-save-info"><Save className="h-4 w-4 mr-1" /> Kaydet</Button>}
            </TabsContent>

            <TabsContent value="egitim" className="space-y-3 pt-3">
              {canEdit && (
                <div className="flex justify-end">
                  <Button variant="outline" size="sm" onClick={openTrash} data-testid="pd-training-trash-btn"><Trash2 className="h-4 w-4 mr-1" /> Çöp Kutusu</Button>
                </div>
              )}
              {canEdit && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <div className="text-sm font-semibold">Yeni Eğitim Kaydı</div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Eğitim Tarihi</Label><Input type="date" value={nt.training_date} onChange={(e) => setNt({ ...nt, training_date: e.target.value })} data-testid="pd-tr-date" /></div>
                    <div className="space-y-1"><Label className="text-xs">Geçerlilik (öneri +2 yıl)</Label><Input type="date" value={nt.valid_until} onChange={(e) => setNt({ ...nt, valid_until: e.target.value })} data-testid="pd-tr-valid" /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[["basic", "Temel Bilinçlendirme"], ["safety", "Emniyet Eğitimi"], ["role", "Göreve Özgü"]].map(([k, l]) => (
                      <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label><Input type="number" min="0" max="100" value={nt[k]} onChange={(e) => setNt({ ...nt, [k]: e.target.value })} data-testid={`pd-tr-${k}`} /></div>
                    ))}
                  </div>
                  <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={addTraining} data-testid="pd-add-training"><Plus className="h-4 w-4 mr-1" /> Ekle</Button>
                </div>
              )}
              {trainings.length === 0 ? <p className="text-sm text-slate-400">Eğitim kaydı yok.</p> : trainings.map((t) => (
                <div key={t.id} className="rounded border border-slate-200 p-2.5 text-sm" data-testid={`pd-training-${t.id}`}>
                  <div className="flex justify-between items-center gap-2">
                    <span>{t.training_date ? formatDate(t.training_date) : "Tarih girilmedi"} → {t.valid_until ? formatDate(t.valid_until) : "—"}</span>
                    <div className="flex items-center gap-1">
                      {t.completed ? <StatusBadge tone="active">Tamamlandı</StatusBadge> : (canEdit && <Button size="sm" variant="outline" onClick={() => complete(t.id)} data-testid={`pd-complete-${t.id}`}>Tamamla</Button>)}
                      {canEdit && <>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openEdit(t)} data-testid={`pd-edit-${t.id}`} title="Düzenle"><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 text-rose-600" onClick={() => setDelTr(t)} data-testid={`pd-delete-${t.id}`} title="Sil"><Trash2 className="h-4 w-4" /></Button>
                      </>}
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 mt-1">Temel sonuç: <strong>{t.basic_result ?? "—"}</strong> · Uzman sonuç: <strong>{t.expert_result ?? "—"}</strong></div>
                  <div className="text-[11px] text-slate-400">Eğitmen: {t.trainer_name || "—"} / {t.trainer_org || "—"}</div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="belge" className="space-y-3 pt-3">
              {canEdit && (
                <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                  <div className="text-sm font-semibold">Sertifika / Test PDF Yükle</div>
                  <select className="border rounded px-2 py-1 text-sm w-full" value={certType} onChange={(e) => setCertType(e.target.value)} data-testid="pd-cert-type">
                    <option value="temel">Temel Eğitim Sertifikası</option>
                    <option value="uzman">Uzman/Göreve Özgü Eğitim Sertifikası</option>
                    <option value="test_temel">TMGD Temel Bilinçlendirme Testi</option>
                    <option value="test_emniyet">Emniyet Eğitimi Testi</option>
                    <option value="test_gorev">Göreve Özgü Eğitim Testi</option>
                    <option value="diger">Diğer Eğitim Belgesi</option>
                  </select>
                  <Input type="file" accept=".pdf,.docx,.xlsx" onChange={(e) => setCertFile(e.target.files?.[0] || null)} data-testid="pd-cert-file" />
                  <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={uploadCert} data-testid="pd-cert-upload"><Upload className="h-4 w-4 mr-1" /> Yükle & Yayımla</Button>
                </div>
              )}
              {docs.length === 0 ? <p className="text-sm text-slate-400">Belge yok.</p> : docs.map((d) => (
                <div key={d.id} className="flex justify-between items-center py-1.5 border-b border-slate-100 text-sm" data-testid={`pd-doc-${d.id}`}>
                  <span className="flex items-center gap-1.5">{d.name}.{d.ext} {d.cert_label && <StatusBadge tone="info">{d.cert_label}</StatusBadge>}{d.training_date && <span className="text-xs text-slate-400">· Eğitim {formatDate(d.training_date)}</span>}</span>
                  <div className="flex gap-1">
                    {d.ext === "pdf" && <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => previewFile(`/documents/${d.id}/download`)} data-testid={`pd-doc-preview-${d.id}`}><Eye className="h-4 w-4" /></Button>}
                    <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => downloadFile(`/documents/${d.id}/download`, `${d.name}.${d.ext}`)}><Download className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="src5" className="space-y-3 pt-3">
              {p.valid_src5 && <StatusBadge tone="info">Geçerli SRC5 — eğitim takibi dışında</StatusBadge>}
              <div className="grid grid-cols-3 gap-2">
                <div className="space-y-1"><Label className="text-xs">SRC5 No</Label><Input value={src5.number || ""} onChange={(e) => setSrc5({ ...src5, number: e.target.value })} disabled={!canEdit} data-testid="pd-src5-number" /></div>
                <div className="space-y-1"><Label className="text-xs">Belge Tarihi</Label><Input type="date" value={src5.doc_date || ""} onChange={(e) => setSrc5({ ...src5, doc_date: e.target.value })} disabled={!canEdit} data-testid="pd-src5-date" /></div>
                <div className="space-y-1"><Label className="text-xs">Geçerlilik</Label><Input type="date" value={src5.valid_until || ""} onChange={(e) => setSrc5({ ...src5, valid_until: e.target.value })} disabled={!canEdit} data-testid="pd-src5-valid" /></div>
              </div>
              {canEdit && <Button className="bg-sky-600 hover:bg-sky-700" onClick={saveSrc5} data-testid="pd-save-src5"><Save className="h-4 w-4 mr-1" /> SRC5 Kaydet</Button>}

              <div className="rounded-lg border border-slate-200 p-3 space-y-2">
                <div className="text-sm font-semibold">SRC5 Belgesi (PDF)</div>
                {p.src5_doc ? (
                  <div className="flex items-center justify-between text-sm" data-testid="pd-src5-doc">
                    <span className="truncate">{p.src5_doc.name}.pdf {p.src5_doc.published ? <StatusBadge tone="active">Yayımda</StatusBadge> : <StatusBadge tone="muted">Taslak</StatusBadge>}</span>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => previewFile(`/documents/${p.src5_doc.id}/download`)} data-testid="pd-src5-preview"><Eye className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => downloadFile(`/documents/${p.src5_doc.id}/download`, `${p.src5_doc.name}.pdf`)}><Download className="h-4 w-4" /></Button>
                      {canEdit && <Button variant="ghost" size="sm" className={`h-7 px-2 text-xs ${p.src5_doc.published ? "text-emerald-600" : "text-sky-600"}`} onClick={toggleSrc5Publish} data-testid="pd-src5-publish">{p.src5_doc.published ? "Geri al" : "Yayımla"}</Button>}
                    </div>
                  </div>
                ) : <p className="text-xs text-slate-400">Henüz SRC5 belgesi yüklenmedi.</p>}
                {canEdit && (
                  <div className="space-y-2">
                    <Input type="file" accept=".pdf" onChange={(e) => setSrc5File(e.target.files?.[0] || null)} data-testid="pd-src5-file" />
                    <label className="flex items-center gap-2 text-xs text-slate-600"><input type="checkbox" checked={src5Publish} onChange={(e) => setSrc5Publish(e.target.checked)} data-testid="pd-src5-publish-check" /> Müşteri görebilsin (yayımla)</label>
                    <Button size="sm" className="bg-sky-600 hover:bg-sky-700" onClick={uploadSrc5Doc} data-testid="pd-src5-upload"><Upload className="h-4 w-4 mr-1" /> {p.src5_doc ? "Yeni Sürüm Yükle" : "Yükle"}</Button>
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        )}
      </DialogContent>

      {/* Eğitim düzenle */}
      <Dlg open={!!editTr} onOpenChange={(o) => !o && setEditTr(null)}>
        <DlgContent>
          <DlgHeader><DlgTitle className="font-display">Eğitim Kaydını Düzenle</DlgTitle></DlgHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <div className="space-y-1"><Label className="text-xs">Eğitim Tarihi</Label><Input type="date" value={ef.training_date} onChange={(e) => setEf({ ...ef, training_date: e.target.value })} data-testid="pd-edit-date" /></div>
              <div className="space-y-1"><Label className="text-xs">Geçerlilik</Label><Input type="date" value={ef.valid_until} onChange={(e) => setEf({ ...ef, valid_until: e.target.value })} data-testid="pd-edit-valid" /></div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {[["basic", "Temel Bilinçlendirme"], ["safety", "Emniyet Eğitimi"], ["role", "Göreve Özgü"]].map(([k, l]) => (
                <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label><Input type="number" min="0" max="100" value={ef[k]} onChange={(e) => setEf({ ...ef, [k]: e.target.value })} data-testid={`pd-edit-${k}`} /></div>
              ))}
            </div>
            <p className="text-xs text-slate-400">Temel sonuç ilk iki testin ortalaması (aşağı yuvarlanır), uzman sonuç Göreve Özgü testidir; eksik puanda hesaplanmaz.</p>
          </div>
          <DlgFooter><Button className="bg-sky-600 hover:bg-sky-700" onClick={saveEdit} data-testid="pd-edit-save"><Save className="h-4 w-4 mr-1" /> Kaydet</Button></DlgFooter>
        </DlgContent>
      </Dlg>

      {/* Eğitim sil onay */}
      <Dlg open={!!delTr} onOpenChange={(o) => !o && setDelTr(null)}>
        <DlgContent>
          <DlgHeader><DlgTitle className="font-display">Eğitim Kaydını Sil</DlgTitle></DlgHeader>
          <p className="text-sm text-slate-600"><span className="font-medium">{p?.name} {p?.surname}</span> — {delTr?.training_date ? formatDate(delTr.training_date) : "tarih girilmedi"} tarihli eğitim kaydını silmek istediğinize emin misiniz? Kayıt 30 günlük çöp kutusuna taşınır ve geri alınabilir. Bağlı belgeler klasörlerinde korunur.</p>
          <DlgFooter>
            <Button variant="outline" onClick={() => setDelTr(null)} data-testid="pd-delete-cancel">Vazgeç</Button>
            <Button className="bg-rose-600 hover:bg-rose-700" onClick={doDelete} data-testid="pd-delete-ok">Sil</Button>
          </DlgFooter>
        </DlgContent>
      </Dlg>

      {/* Eğitim çöp kutusu */}
      <Dlg open={trash !== null} onOpenChange={(o) => !o && setTrash(null)}>
        <DlgContent>
          <DlgHeader><DlgTitle className="font-display">Silinmiş Eğitim Kayıtları (30 gün)</DlgTitle></DlgHeader>
          <div className="max-h-96 overflow-y-auto divide-y divide-slate-100">
            {(trash || []).length === 0 ? <p className="text-sm text-slate-500 py-2">Boş.</p> : (trash || []).map((t) => (
              <div key={t.id} className="flex items-center justify-between py-2 text-sm" data-testid={`pd-trash-${t.id}`}>
                <span>{t.training_date ? formatDate(t.training_date) : "Tarih girilmedi"}<span className="text-xs text-slate-400 ml-2">{t.days_remaining} gün</span></span>
                <Button variant="outline" size="sm" onClick={() => restoreTr(t.id)} data-testid={`pd-trash-restore-${t.id}`}><RotateCcw className="h-4 w-4 mr-1" /> Geri Yükle</Button>
              </div>
            ))}
          </div>
        </DlgContent>
      </Dlg>
    </Dialog>
  );
}
